import React from 'react';
import { Col, Row, Text, Button } from 'components';
import { FontAwesome5 } from '@expo/vector-icons';
import { StyleSheet } from 'react-native';

const MSC_BLUE = '#004B8D';
const SUCCESS = '#1FA463';
const WARNING = '#F5A623';
const ERROR = '#E94A36';

export interface IssueCardData {
  id: string;
  orderNumber: string;
  customer: string;
  status: 'PENDING' | 'IN_REVIEW' | 'RESOLVED';
  message: string;
  channel: 'whatsapp' | 'phone' | 'email';
  timeAgo: string;
  isPositive?: boolean;
}

interface IssueCardProps {
  issue: IssueCardData;
  onAction?: (issueId: string, action: string) => void;
}

const IssueCard: React.FC<IssueCardProps> = ({ issue, onAction }) => {
  const getIssueStatusColor = (status: IssueCardData['status']) => {
    switch (status) {
      case 'PENDING':
        return ERROR;
      case 'IN_REVIEW':
        return WARNING;
      case 'RESOLVED':
        return SUCCESS;
      default:
        return ERROR;
    }
  };

  const getIssueStatusBgColor = (status: IssueCardData['status']) => {
    switch (status) {
      case 'PENDING':
        return '#FFEBE8';
      case 'IN_REVIEW':
        return '#FFF8E6';
      case 'RESOLVED':
        return '#E6F7ED';
      default:
        return '#FFEBE8';
    }
  };

  const statusColor = getIssueStatusColor(issue.status);
  const bgColor = getIssueStatusBgColor(issue.status);
  const isResolved = issue.status === 'RESOLVED';
  const isPending = issue.status === 'PENDING';

  const getChannelIcon = () => {
    switch (issue.channel) {
      case 'whatsapp':
        return <FontAwesome5 name="whatsapp" size={14} color="#25D366" />;
      case 'phone':
        return <FontAwesome5 name="phone" size={14} color={MSC_BLUE} />;
      case 'email':
        return <FontAwesome5 name="envelope" size={14} color={MSC_BLUE} />;
      default:
        return null;
    }
  };

  const getActionText = () => {
    if (issue.status === 'IN_REVIEW') return 'UPDATE';
    if (issue.status === 'PENDING' && issue.channel === 'email') return 'INVESTIGATE';
    return 'RESOLVE';
  };

  return (
    <Col
      style={[
        styles.issueCard,
        {
          borderLeftColor: statusColor,
          backgroundColor: bgColor,
          opacity: isResolved ? 0.75 : 1,
        },
        isPending && styles.pulseAlert,
      ]}
    >
      <Row justifyContent="space-between" alignItems="flex-start" marginBottom={12}>
        <Col flex1>
          <Text fontSize={18} bold color="#1F2937">
            {issue.orderNumber}
          </Text>
          <Text fontSize={14} color="#4B5563" medium marginTop={4}>
            {issue.customer}
          </Text>
        </Col>
        <Col
          style={[
            styles.statusBadge,
            {
              backgroundColor: statusColor,
            },
          ]}
        >
          <Text fontSize={12} bold color="white" style={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {issue.status.replace('_', ' ')}
          </Text>
        </Col>
      </Row>

      <Col
        style={[
          styles.issueMessage,
          {
            borderLeftColor: statusColor,
          },
        ]}
        marginBottom={12}
      >
        <Text fontSize={14} color="#374151" style={{ fontStyle: 'italic' }}>
          "{issue.message}"
        </Text>
      </Col>

      <Row justifyContent="space-between" alignItems="center">
        <Row alignItems="center">
          {getChannelIcon()}
          <Text fontSize={12} color="#6B7280" marginLeft={8}>
            {/* {issue.channel.charAt(0).toUpperCase() + issue.channel.slice(1)} • {issue.timeAgo} */}
            {issue.timeAgo}
          </Text>
        </Row>
        {isResolved ? (
          <Row alignItems="center">
            <FontAwesome5
              name={issue.isPositive ? 'thumbs-up' : 'check-circle'}
              size={14}
              color={SUCCESS}
              style={{ marginRight: 4 }}
            />
            <Text fontSize={12} bold style={{ color: SUCCESS }}>
              {issue.isPositive ? 'POSITIVE' : 'RESOLVED'}
            </Text>
          </Row>
        ) : (
          <Button
            text={getActionText()}
            fontSize={12}
            paddingVertical={6}
            paddingHorizontal={12}
            borderRadius={20}
            backgroundColor={statusColor}
            onPress={() => {
              if (onAction) {
                onAction(issue.id, getActionText());
              }
            }}
          />
        )}
      </Row>
    </Col>
  );
};

const styles = StyleSheet.create({
  issueCard: {
    padding: 20,
    borderRadius: 12,
    borderLeftWidth: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  statusBadge: {
    paddingVertical: 4,
    paddingHorizontal: 12,
    borderRadius: 20,
  },
  pulseAlert: {
    opacity: 1,
  },
  issueMessage: {
    backgroundColor: '#F9FAFB',
    padding: 12,
    borderRadius: 8,
    borderLeftWidth: 2,
  },
});

export default IssueCard;

